/**
 * Assets Page
 * Client infrastructure inventory with Add Asset capability.
 */
import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { assetApi } from '../api/client';
import {
  Server, Wifi, WifiOff, AlertTriangle, Plus, X, CheckCircle2,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const criticalityColors = {
  critical: 'bg-red-500/15 text-red-400 border-red-500/25',
  high: 'bg-amber-500/15 text-amber-400 border-amber-500/25',
  medium: 'bg-blue-500/15 text-blue-400 border-blue-500/25',
  low: 'bg-gray-500/15 text-gray-400 border-gray-500/25',
};

const statusConfig = {
  active: { icon: Wifi, color: 'text-emerald-400', label: 'Active' },
  degraded: { icon: AlertTriangle, color: 'text-amber-400', label: 'Degraded' },
  offline: { icon: WifiOff, color: 'text-red-400', label: 'Offline' },
  maintenance: { icon: Server, color: 'text-blue-400', label: 'Maintenance' },
  decommissioned: { icon: Server, color: 'text-gray-600', label: 'Decommissioned' },
};

const assetTypes = [
  'server', 'workstation', 'switch', 'router', 'firewall',
  'cad_system', 'rms', 'dispatch_console', 'radio_console',
  'mdt', 'ups', 'wireless_controller', 'av_system', 'other',
];

const emptyForm = {
  name: '', asset_type: 'server', criticality: 'medium', status: 'active',
  ip_address: '', hostname: '', os_info: '', location: '',
  make: '', model: '', serial_number: '', notes: '',
};

export default function AssetsPage() {
  const { isSentinelUser } = useAuth();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  async function loadAssets() {
    try {
      const res = await assetApi.list({ page_size: 100 });
      setAssets(res.data.items || res.data || []);
    } catch (err) {
      console.error('Failed to load assets:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadAssets(); }, []);

  const update = (field, value) => setForm({ ...form, [field]: value });

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required'); return; }

    setSubmitting(true);
    setError('');
    try {
      const payload = { ...form };
      if (!payload.ip_address) delete payload.ip_address;
      await assetApi.create(payload);
      setShowModal(false);
      setForm({ ...emptyForm });
      setSuccess('Asset created successfully');
      setTimeout(() => setSuccess(''), 3000);
      await loadAssets();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create asset');
    } finally {
      setSubmitting(false);
    }
  }

  const canAdd = isSentinelUser;

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-white">Assets</h1>
          <p className="text-sm text-gray-500 mt-1">Tracked infrastructure and systems</p>
        </div>
        {canAdd && (
          <button
            onClick={() => { setShowModal(true); setError(''); }}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Asset
          </button>
        )}
      </div>

      {success && (
        <div className="mb-4 px-4 py-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-sm text-emerald-400 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" /> {success}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-36 bg-gray-800 rounded-xl animate-pulse" />
          ))
        ) : assets.length === 0 ? (
          <div className="col-span-3 text-center py-16 text-sm text-gray-500">
            No assets registered yet. {canAdd ? 'Click "Add Asset" to get started.' : 'Assets will appear here as they are added.'}
          </div>
        ) : (
          assets.map((asset) => {
            const status = statusConfig[asset.status] || statusConfig.active;
            const StatusIcon = status.icon;
            const crit = criticalityColors[asset.criticality] || criticalityColors.medium;

            return (
              <div key={asset.id}
                className="bg-gray-900 border border-gray-800 rounded-xl p-5 hover:border-gray-700 transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <StatusIcon className={`w-4 h-4 ${status.color}`} />
                    <span className={`text-xs ${status.color}`}>{status.label}</span>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider border ${crit}`}>
                    {asset.criticality}
                  </span>
                </div>
                <h3 className="text-sm font-medium text-gray-200 mb-1">{asset.name}</h3>
                <p className="text-xs text-gray-500 mb-3">{asset.asset_type.replace(/_/g, ' ')}</p>
                <div className="space-y-1 text-xs text-gray-500">
                  {asset.hostname && <div>Host: {asset.hostname}</div>}
                  {asset.ip_address && <div>IP: {asset.ip_address}</div>}
                  {asset.location && <div>Location: {asset.location}</div>}
                  {asset.make && <div>{asset.make} {asset.model || ''}</div>}
                  {asset.last_seen_at && (
                    <div>Last seen: {formatDistanceToNow(new Date(asset.last_seen_at), { addSuffix: true })}</div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Add Asset Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-gray-800">
              <h2 className="text-lg font-semibold text-white">Add Asset</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-300">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {error && (
                <div className="px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {error}
                </div>
              )}

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1.5">Name *</label>
                <input type="text" value={form.name} onChange={(e) => update('name', e.target.value)}
                  placeholder="e.g., CAD Primary Server"
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-gray-600" />
              </div>

              {/* Type + Criticality + Status row */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1.5">Type</label>
                  <select value={form.asset_type} onChange={(e) => update('asset_type', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 focus:outline-none focus:border-gray-600">
                    {assetTypes.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1.5">Criticality</label>
                  <select value={form.criticality} onChange={(e) => update('criticality', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 focus:outline-none focus:border-gray-600">
                    {['critical', 'high', 'medium', 'low'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1.5">Status</label>
                  <select value={form.status} onChange={(e) => update('status', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 focus:outline-none focus:border-gray-600">
                    {['active', 'degraded', 'offline', 'maintenance'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              {/* Network row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1.5">Hostname</label>
                  <input type="text" value={form.hostname} onChange={(e) => update('hostname', e.target.value)}
                    placeholder="e.g., LCSO-CAD-01"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-gray-600" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1.5">IP Address</label>
                  <input type="text" value={form.ip_address} onChange={(e) => update('ip_address', e.target.value)}
                    placeholder="e.g., 10.10.1.10"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-gray-600" />
                </div>
              </div>

              {/* OS + Location */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1.5">OS / Firmware</label>
                  <input type="text" value={form.os_info} onChange={(e) => update('os_info', e.target.value)}
                    placeholder="e.g., Windows Server 2022"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-gray-600" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1.5">Location</label>
                  <input type="text" value={form.location} onChange={(e) => update('location', e.target.value)}
                    placeholder="e.g., Server Room A"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-gray-600" />
                </div>
              </div>

              {/* Make + Model + Serial */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1.5">Make</label>
                  <input type="text" value={form.make} onChange={(e) => update('make', e.target.value)}
                    placeholder="e.g., Dell"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-gray-600" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1.5">Model</label>
                  <input type="text" value={form.model} onChange={(e) => update('model', e.target.value)}
                    placeholder="e.g., PowerEdge R750"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-gray-600" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1.5">Serial #</label>
                  <input type="text" value={form.serial_number} onChange={(e) => update('serial_number', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-gray-600" />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1.5">Notes</label>
                <textarea value={form.notes} onChange={(e) => update('notes', e.target.value)}
                  rows={2} placeholder="Any additional details..."
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-gray-600 resize-none" />
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-800">
                <button type="button" onClick={() => setShowModal(false)}
                  className="px-4 py-2.5 text-sm text-gray-400 hover:text-gray-200 transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={submitting}
                  className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-sm font-medium rounded-lg transition-colors">
                  {submitting ? 'Creating...' : 'Add Asset'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
